import React, { useState, useEffect } from 'react';
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    employee_name: '',
    trade: '',
    charge_rate: '',
    bill_rate: '',
    status: 'active'
  });
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [employeesPerPage] = useState(20);

  // Integration state
  const [showRetrieveModal, setShowRetrieveModal] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [retrieveIntegration, setRetrieveIntegration] = useState(null);
  const [retrieving, setRetrieving] = useState(false);
  
  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [retrievedEmployees, setRetrievedEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Project rates modal state
  const [showProjectRatesModal, setShowProjectRatesModal] = useState(false);
  const [activeProjects, setActiveProjects] = useState([]);
  const [projectRates, setProjectRates] = useState([]);
  const [loadingProjectRates, setLoadingProjectRates] = useState(false);
  const [savingProjectRates, setSavingProjectRates] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);
  
  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${baseURL}/api/integrations`);
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };
  
  const handleRetrieveModal = () => {
    setRetrieveIntegration(null);
    setShowRetrieveModal(true);
    fetchIntegrations();
  };
  
  const handleRetrieveEmployees = async () => {
    if (!retrieveIntegration) {
      alert('Please select an integration');
      return;
    }

    setRetrieving(true);

    try {
      const response = await axios.get(`${baseURL}/api/integrations/${retrieveIntegration.vuid}/retrieve-employees`);
      const data = response.data;
      
      if (data.success && data.employees) {
        // Filter out already imported employees
        const newEmployees = data.employees.filter(emp => !emp.already_imported);
        
        if (newEmployees.length === 0) {
          let message = `Retrieved ${data.count} employees. `;
          if (data.count === 0) {
            message += `No employees found.`;
          } else {
            message += `All have already been imported.`;
          }
          if (data.errors && data.errors.length > 0) {
            message += `\n\nNote: Some errors occurred during retrieval.`;
          }
          alert(message);
          setShowRetrieveModal(false);
          setRetrieveIntegration(null);
          return;
        }
        
        // Set retrieved employees and show import modal
        setRetrievedEmployees(newEmployees);
        setSelectedEmployees([]);
        setShowRetrieveModal(false);
        setShowImportModal(true);
        // Keep retrieveIntegration for the import endpoint
      } else {
        alert(`Error: ${data.error || 'Failed to retrieve employees'}`);
      }
    } catch (error) {
      console.error('Error retrieving employees:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to retrieve employees from integration';
      alert(`Error: ${errorMsg}`);
    } finally {
      setRetrieving(false);
    }
  };
  
  const handleEmployeeSelection = (employee, selected) => {
    if (selected) {
      setSelectedEmployees([...selectedEmployees, employee]);
    } else {
      setSelectedEmployees(selectedEmployees.filter(emp => emp.procore_user_id !== employee.procore_user_id));
    }
  };
  
  const handleSelectAllEmployees = () => {
    setSelectedEmployees([...retrievedEmployees]);
  };
  
  const handleClearAllEmployees = () => {
    setSelectedEmployees([]);
  };
  
  const handleImportEmployees = async () => {
    if (selectedEmployees.length === 0) {
      alert('Please select at least one employee to import');
      return;
    }

    if (!retrieveIntegration) {
      alert('Integration context missing');
      return;
    }

    setImporting(true);

    try {
      // Import employees using the import endpoint
      const response = await axios.post(
        `${baseURL}/api/integrations/${retrieveIntegration.vuid}/import-employees`,
        { employees: selectedEmployees }
      );
      
      const data = response.data;
      
      if (data.success) {
        let message = `Successfully imported employees!\n\n` +
          `Created: ${data.employees_created}\n` +
          `Updated: ${data.employees_updated}\n` +
          `Total Processed: ${data.total_processed}`;
        
        if (data.errors && data.errors.length > 0) {
          message += `\n\nErrors:\n${data.errors.join('\n')}`;
        }
        
        alert(message);
        
        // Refresh employees and close modal
        await fetchEmployees();
        setShowImportModal(false);
        setRetrievedEmployees([]);
        setSelectedEmployees([]);
        setRetrieveIntegration(null);
      } else {
        alert(`Error: ${data.error || 'Failed to import employees'}`);
      }
    } catch (error) {
      console.error('Error importing employees:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to import employees';
      alert(`Error: ${errorMsg}`);
    } finally {
      setImporting(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseURL}/api/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.employee_id.trim()) {
      newErrors.employee_id = 'Employee ID is required';
    }
    
    if (!formData.employee_name.trim()) {
      newErrors.employee_name = 'Employee name is required';
    }
    
    if (formData.charge_rate && isNaN(parseFloat(formData.charge_rate))) {
      newErrors.charge_rate = 'Charge rate must be a valid number';
    }
    
    if (formData.bill_rate && isNaN(parseFloat(formData.bill_rate))) {
      newErrors.bill_rate = 'Bill rate must be a valid number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const employeeData = {
        ...formData,
        charge_rate: formData.charge_rate ? parseFloat(formData.charge_rate) : null,
        bill_rate: formData.bill_rate ? parseFloat(formData.bill_rate) : null
      };
      
      if (editingEmployee) {
        await axios.put(`${baseURL}/api/employees/${editingEmployee.vuid}`, employeeData);
      } else {
        await axios.post(`${baseURL}/api/employees`, employeeData);
      }
      
      await fetchEmployees();
      resetForm();
    } catch (error) {
      console.error('Error saving employee:', error);
      if (error.response?.data?.error) {
        setErrors({ submit: error.response.data.error });
      }
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      employee_id: employee.employee_id,
      employee_name: employee.employee_name,
      trade: employee.trade || '',
      charge_rate: employee.charge_rate || '',
      bill_rate: employee.bill_rate || '',
      status: employee.status
    });
    setShowCreateForm(true);
  };

  const handleOpenProjectRatesModal = async () => {
    if (!editingEmployee) return;
    
    setShowProjectRatesModal(true);
    setLoadingProjectRates(true);
    
    try {
      // Fetch active projects
      const projectsResponse = await axios.get(`${baseURL}/api/projects`);
      const allProjects = projectsResponse.data || [];
      const active = allProjects.filter(p => p.status === 'active');
      setActiveProjects(active);
      
      // Fetch existing project rates
      const ratesResponse = await axios.get(`${baseURL}/api/employees/${editingEmployee.vuid}/project-rates`);
      const existingRates = ratesResponse.data || [];
      
      // Initialize project rates with defaults from employee record
      const defaultChargeRate = parseFloat(editingEmployee.charge_rate) || 0;
      const defaultBillRate = parseFloat(editingEmployee.bill_rate) || 0;
      
      const ratesMap = {};
      existingRates.forEach(rate => {
        ratesMap[rate.project_vuid] = {
          vuid: rate.vuid,
          charge_rate: rate.charge_rate !== null ? rate.charge_rate : defaultChargeRate,
          bill_rate: rate.bill_rate !== null ? rate.bill_rate : defaultBillRate
        };
      });
      
      // Create rates array with all active projects
      const rates = active.map(project => {
        if (ratesMap[project.vuid]) {
          return {
            ...ratesMap[project.vuid],
            project_vuid: project.vuid,
            project_number: project.project_number,
            project_name: project.project_name
          };
        } else {
          return {
            project_vuid: project.vuid,
            project_number: project.project_number,
            project_name: project.project_name,
            charge_rate: defaultChargeRate,
            bill_rate: defaultBillRate
          };
        }
      });
      
      setProjectRates(rates);
    } catch (error) {
      console.error('Error loading project rates:', error);
      alert('Error loading project rates: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingProjectRates(false);
    }
  };

  const handleProjectRateChange = (projectVuid, field, value) => {
    setProjectRates(prev => prev.map(rate => 
      rate.project_vuid === projectVuid 
        ? { ...rate, [field]: parseFloat(value) || 0 }
        : rate
    ));
  };

  const handleSaveProjectRates = async () => {
    if (!editingEmployee) return;
    
    setSavingProjectRates(true);
    
    try {
      // Only send rates that differ from default or have existing records
      const defaultChargeRate = parseFloat(editingEmployee.charge_rate) || 0;
      const defaultBillRate = parseFloat(editingEmployee.bill_rate) || 0;
      
      const ratesToSave = projectRates.map(rate => ({
        project_vuid: rate.project_vuid,
        charge_rate: rate.charge_rate !== defaultChargeRate ? rate.charge_rate : null,
        bill_rate: rate.bill_rate !== defaultBillRate ? rate.bill_rate : null
      })).filter(rate => rate.charge_rate !== null || rate.bill_rate !== null);
      
      await axios.post(
        `${baseURL}/api/employees/${editingEmployee.vuid}/project-rates`,
        { rates: ratesToSave }
      );
      
      alert('Project rates saved successfully!');
      setShowProjectRatesModal(false);
    } catch (error) {
      console.error('Error saving project rates:', error);
      alert('Error saving project rates: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingProjectRates(false);
    }
  };

  const handleDelete = async (employee) => {
    if (window.confirm(`Are you sure you want to delete employee ${employee.employee_name}?`)) {
      try {
        await axios.delete(`${baseURL}/api/employees/${employee.vuid}`);
        await fetchEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
        if (error.response?.data?.error) {
          alert(error.response.data.error);
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      employee_name: '',
      trade: '',
      charge_rate: '',
      bill_rate: '',
      status: 'active'
    });
    setErrors({});
    setEditingEmployee(null);
    setShowCreateForm(false);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter employees based on search term and status
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = 
      employee.employee_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.trade && employee.trade.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || employee.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastEmployee = currentPage * employeesPerPage;
  const indexOfFirstEmployee = indexOfLastEmployee - employeesPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstEmployee, indexOfLastEmployee);
  const totalPages = Math.ceil(filteredEmployees.length / employeesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vermillion-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employees</h1>
          <p className="text-gray-600 mt-1">Manage employee information and rates</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleRetrieveModal}
            className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
            style={{ color: '#7F1D1D' }}
          >
            Retrieve from Integration
          </button>
        <button
          onClick={() => setShowCreateForm(true)}
            className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
            style={{ color: '#7F1D1D' }}
        >
          Add Employee
        </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">
            {editingEmployee ? 'Edit Employee' : 'Create New Employee'}
          </h2>
          
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {errors.submit}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee ID *
                </label>
                <input
                  type="text"
                  name="employee_id"
                  value={formData.employee_id}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 ${
                    errors.employee_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., EMP001"
                />
                {errors.employee_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.employee_id}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee Name *
                </label>
                <input
                  type="text"
                  name="employee_name"
                  value={formData.employee_name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 ${
                    errors.employee_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Full name"
                />
                {errors.employee_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.employee_name}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trade
                </label>
                <input
                  type="text"
                  name="trade"
                  value={formData.trade}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500"
                  placeholder="e.g., Carpenter, Electrician"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Charge Rate
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="charge_rate"
                  value={formData.charge_rate}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 ${
                    errors.charge_rate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.charge_rate && (
                  <p className="text-red-500 text-sm mt-1">{errors.charge_rate}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bill Rate
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="bill_rate"
                  value={formData.bill_rate}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 ${
                    errors.bill_rate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.bill_rate && (
                  <p className="text-red-500 text-sm mt-1">{errors.bill_rate}</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-between items-center pt-4">
              {editingEmployee && (
                <button
                  type="button"
                  onClick={handleOpenProjectRatesModal}
                  className="px-4 py-2 bg-gray-50 hover:bg-gray-100 font-semibold rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                  style={{ color: '#7F1D1D' }}
                >
                  Manage Project Rates
                </button>
              )}
              <div className="flex justify-end space-x-3 ml-auto">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-vermillion-600 hover:bg-vermillion-700 text-white rounded-md transition-colors"
              >
                {editingEmployee ? 'Update Employee' : 'Create Employee'}
              </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Employees Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Charge Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bill Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentEmployees.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    {filteredEmployees.length === 0 ? 'No employees found' : 'No employees on this page'}
                  </td>
                </tr>
              ) : (
                currentEmployees.map((employee) => (
                  <tr key={employee.vuid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {employee.employee_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {employee.employee_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {employee.trade || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(employee.charge_rate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(employee.bill_rate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(employee.status)}`}>
                        {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(employee)}
                          className="text-vermillion-600 hover:text-vermillion-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(employee)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstEmployee + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastEmployee, filteredEmployees.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredEmployees.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === number
                          ? 'z-10 bg-vermillion-50 border-vermillion-500 text-vermillion-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {number}
                    </button>
                  ))}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Retrieve Employees Modal */}
      {showRetrieveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Retrieve Employees</h2>
              <button
                onClick={() => {
                  setShowRetrieveModal(false);
                  setRetrieveIntegration(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <p className="text-gray-600">
                  Select an integration to retrieve employees from external systems.
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-lg font-semibold text-gray-900 mb-3">
                  Select Integration *
                </label>
                {integrations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-lg">No integrations available</p>
                    <p className="text-sm mt-2">Create an integration first to retrieve employees from external systems.</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {integrations.filter(i => i.integration_type === 'procore' && i.status === 'active').map((integration) => (
                      <div
                        key={integration.vuid}
                        onClick={() => setRetrieveIntegration(integration)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          retrieveIntegration?.vuid === integration.vuid
                            ? 'border-gray-800 bg-gray-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">{integration.integration_name}</h4>
                            <p className="text-sm text-gray-600 capitalize">{integration.integration_type.replace('_', ' ')}</p>
                            {integration.base_url && (
                              <p className="text-xs text-gray-500 mt-1">{integration.base_url}</p>
                            )}
                          </div>
                          {retrieveIntegration?.vuid === integration.vuid && (
                            <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowRetrieveModal(false);
                    setRetrieveIntegration(null);
                  }}
                  className="px-8 py-4 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRetrieveEmployees}
                  disabled={!retrieveIntegration || retrieving}
                  className={`px-8 py-4 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg ${
                    retrieveIntegration && !retrieving
                      ? 'bg-gray-800 text-white hover:bg-gray-900'
                      : retrieving
                      ? 'bg-gray-800 text-white cursor-wait'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {retrieving ? (
                    <div className="flex items-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Retrieving...</span>
                    </div>
                  ) : (
                    'Retrieve Employees'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Employees Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Import Employees from Procore</h2>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setRetrievedEmployees([]);
                  setSelectedEmployees([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Summary */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-blue-900">
                      {retrievedEmployees.length} Employees Available for Import
                    </h3>
                    <p className="text-blue-700 text-sm mt-1">
                      Select the employees you want to import from Procore.
                    </p>
                    <p className="text-blue-700 mt-2">
                      Selected: {selectedEmployees.length} employee(s)
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSelectAllEmployees}
                      className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 transform hover:scale-105 shadow-lg"
                    >
                      Select All
                    </button>
                    <button
                      onClick={handleClearAllEmployees}
                      className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 transform hover:scale-105 shadow-lg"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>

              {/* Employees List */}
              <div className="space-y-3">
                {retrievedEmployees.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No employees available to import.</p>
                  </div>
                ) : (
                  retrievedEmployees.map((employee, index) => {
                    const isSelected = selectedEmployees.some(emp => emp.procore_user_id === employee.procore_user_id);
                    
                    return (
                      <div
                        key={employee.procore_user_id || index}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => handleEmployeeSelection(employee, !isSelected)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => handleEmployeeSelection(employee, e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  {employee.employee_name || 'Unknown Name'}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {employee.email || employee.employee_id || 'No email/ID'}
                                </p>
                              </div>
                            </div>
                            <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                              {employee.trade && (
                                <div>
                                  <span className="font-medium text-gray-500">Trade:</span>
                                  <span className="ml-1 text-gray-900">{employee.trade}</span>
                                </div>
                              )}
                              {employee.job_title && (
                                <div>
                                  <span className="font-medium text-gray-500">Job Title:</span>
                                  <span className="ml-1 text-gray-900">{employee.job_title}</span>
                                </div>
                              )}
                              <div>
                                <span className="font-medium text-gray-500">Employee ID:</span>
                                <span className="ml-1 text-gray-900">{employee.employee_id}</span>
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <svg className="w-5 h-5 text-blue-600 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-4 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setRetrievedEmployees([]);
                  setSelectedEmployees([]);
                }}
                className="px-8 py-4 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleImportEmployees}
                disabled={selectedEmployees.length === 0 || importing}
                className={`px-8 py-4 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg ${
                  selectedEmployees.length > 0 && !importing
                    ? 'bg-gray-800 text-white hover:bg-gray-900'
                    : importing
                    ? 'bg-gray-800 text-white cursor-wait'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {importing ? (
                  <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Importing...</span>
                  </div>
                ) : (
                  `Import ${selectedEmployees.length} Employee${selectedEmployees.length !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Rates Modal */}
      {showProjectRatesModal && editingEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900">
                Project Rates for {editingEmployee.employee_name}
              </h2>
              <button
                onClick={() => {
                  setShowProjectRatesModal(false);
                  setProjectRates([]);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Default rates: Charge Rate: ${parseFloat(editingEmployee.charge_rate || 0).toFixed(2)} | Bill Rate: ${parseFloat(editingEmployee.bill_rate || 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Set project-specific rates below. If not set, the default rates will be used.
              </p>
            </div>

            {loadingProjectRates ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
              </div>
            ) : projectRates.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>No active projects found.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Project
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Charge Rate ($)
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bill Rate ($)
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {projectRates.map((rate) => (
                        <tr key={rate.project_vuid}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{rate.project_name}</div>
                              <div className="text-sm text-gray-500">{rate.project_number}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <input
                              type="number"
                              step="0.01"
                              value={rate.charge_rate || ''}
                              onChange={(e) => handleProjectRateChange(rate.project_vuid, 'charge_rate', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500"
                              placeholder={parseFloat(editingEmployee.charge_rate || 0).toFixed(2)}
                            />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <input
                              type="number"
                              step="0.01"
                              value={rate.bill_rate || ''}
                              onChange={(e) => handleProjectRateChange(rate.project_vuid, 'bill_rate', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500"
                              placeholder={parseFloat(editingEmployee.bill_rate || 0).toFixed(2)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowProjectRatesModal(false);
                  setProjectRates([]);
                }}
                className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                style={{ color: '#7F1D1D' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProjectRates}
                disabled={loadingProjectRates || savingProjectRates}
                className={`font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg ${
                  loadingProjectRates || savingProjectRates
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                style={!(loadingProjectRates || savingProjectRates) ? { color: '#7F1D1D' } : {}}
              >
                {savingProjectRates ? 'Saving...' : 'Save Rates'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;

